import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import {
	useEmployeeAppRole,
	useEmployeeDevice,
	useEmployeeZoneAssignments,
} from "../hooks/useEmployeeProfile";
import type { EmployeeDetail, EmployeeSchedule } from "../types";

/**
 * Bir fakt ustuni.
 *
 * ⚠️ **`EmployeeProfileHeader.tsx` dagi `Fact` ni EKSPORT QILIB import
 * qilmang** — u o'sha faylda lokal va uni eksportga chiqarish qo'shni
 * blokning hunki bilan to'qnashardi. Uch qatorlik nusxa arzonroq.
 */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="min-w-0" data-testid="fact">
			<div className="text-xs text-[#8a929e] mb-1">{label}</div>
			<div className="text-sm text-[#14171c] truncate">{children}</div>
		</div>
	);
}

/** `YYYY-MM-DD` → `DD.MM.YYYY` (kanvas shakli). */
function sana(qiymat: string | null): string {
	if (!qiymat) {
		return "—";
	}

	const [yil, oy, kun] = qiymat.split("-");

	return yil && oy && kun ? `${kun}.${oy}.${yil}` : qiymat;
}

/**
 * `HH:MM:SS` → `HH:MM`.
 *
 * ⚠️ Postgres `time` ustuni soniya bilan keladi (`types/index.ts`
 * `EmployeeSchedule` izohi) — kesilmasa kanvasdagi «09:00–18:00»
 * o'rniga «09:00:00–18:00:00» chizilardi.
 */
function soat(qiymat: string): string {
	return qiymat.slice(0, 5);
}

function jadvalMatni(schedule: EmployeeSchedule | null): string {
	// ⚠️ `null` — NUQSON EMAS: generator hali shu xodim uchun yugurmagan
	// yoki jadval biriktirilmagan (`types/index.ts` izohi).
	return schedule
		? `${schedule.name} · ${soat(schedule.startTime)}–${soat(schedule.endTime)}`
		: "—";
}

/**
 * Xodim sarlavhasining IKKINCHI fakt qatori — kanvasdagi `.facts` bloki
 * (`XodimProfili.dc.html`, `grid-template-columns: repeat(5, 1fr)`).
 *
 * ⚠️⚠️ **Kanvasda IKKI qatlam bor va ular birlashtirilmaydi.** Yuqorida
 * `.emeta` (Lavozim · Bo'lim · Rahbar · Tabel · Telefon) — u
 * `EmployeeProfileHeader` da. Bu yerda esa alohida `.facts` bloki.
 * To'qqizta ustunni bitta qatorga yig'ish 1440 px da har biriga ~150 px
 * berardi va «Standart 5/2 · 09:00–18:00» hamda hudud ro'yxati
 * `truncate` ostida yo'qolardi.
 *
 * ⚠️⚠️ **`gridTemplateColumns` INLINE beriladi, Tailwind sinfi bilan
 * EMAS.** Tailwind 4 sinf nomlarini STATIK skanerlaydi — `grid-cols-${n}`
 * yozilsa CSS umuman generatsiya qilinmaydi, qator bitta ustunga
 * yig'iladi va konsolda hech narsa chiqmaydi. Ustunlar soni esa
 * foydalanuvchining ruxsatlariga qarab o'zgaradi.
 *
 * ⚠️ **Ustun ko'rinishi RUXSATGA bog'liq, QIYMATGA emas.** Bo'sh qiymat
 * `—` bilan chiziladi va ustunning O'ZI qoladi (aks holda qatorning
 * kengligi xodimdan xodimga sakrardi); ruxsat yo'q bo'lsa esa ustun
 * umuman chizilmaydi — aks holda har profil ochilishida keraksiz 403
 * ketardi.
 */
export function EmployeeFactsRow({ employee }: { employee: EmployeeDetail }) {
	const { t } = useTranslation("employees");
	const { can } = usePermission();

	const { data: hududlar = [] } = useEmployeeZoneAssignments(employee.id);
	const { data: qurilmalar = [] } = useEmployeeDevice(employee.id);
	const { data: rollar = [] } = useEmployeeAppRole(employee.userId);

	// ⚠️ Filtr SERVER hisoblagan `amalda` bo'yicha — `validTo === null`
	// bo'yicha EMAS: muddatli biriktirishda `validTo` kelajakda bo'ladi.
	const amaldagiHududlar = hududlar
		.filter((qator) => qator.amalda)
		.map((qator) => qator.zoneName)
		.join(", ");

	const qurilma = qurilmalar[0];
	const qurilmaMatni = qurilma
		? [qurilma.model, [qurilma.os, qurilma.osVersion].filter(Boolean).join(" ")]
				.filter(Boolean)
				.join(" · ")
		: "";

	const rol = rollar[0];
	const rolMatni = rol ? (rol.role?.name ?? rol.roleId) : "";

	const ustunlar = [
		{ key: "hiredAt", label: t("factsRow.hiredAt"), show: true, value: sana(employee.hiredAt) },
		{
			key: "schedule",
			label: t("factsRow.schedule"),
			show: true,
			value: jadvalMatni(employee.schedule),
		},
		{
			key: "zones",
			label: t("factsRow.zones"),
			show: can("zones", "read"),
			value: amaldagiHududlar || "—",
		},
		{
			key: "device",
			label: t("factsRow.device"),
			show: can("devices", "read"),
			value: qurilmaMatni || "—",
		},
		{
			key: "role",
			label: t("factsRow.role"),
			show: can("role-assignments", "read"),
			value: rolMatni || "—",
		},
	].filter((ustun) => ustun.show);

	return (
		<div
			data-testid="facts-row"
			className="mt-4 grid gap-4 border-t border-[#eeefec] pt-4"
			style={{ gridTemplateColumns: `repeat(${ustunlar.length}, minmax(0,1fr))` }}
		>
			{ustunlar.map((ustun) => (
				<Fact key={ustun.key} label={ustun.label}>
					{ustun.value}
				</Fact>
			))}
		</div>
	);
}
